import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, LogOut } from 'lucide-react';
import UsersTab from '@/components/admin/UsersTab';
import LessonsTab from '@/components/admin/LessonsTab';
import AssignmentsTab from '@/components/admin/AssignmentsTab';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, lessonsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/lessons`)
      ]);
      setUsers(usersRes.data);
      setLessons(lessonsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-8 px-4" data-testid="admin-dashboard">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_reporterspeed/artifacts/9jftsuzl_StenoU%20Skool.png" 
            alt="StenoU" 
            className="h-20 object-contain"
            data-testid="logo"
          />
          <div className="flex items-center gap-2">
            <div className="text-right mr-4">
              <p className="text-sm font-medium">{user?.full_name}</p>
              <p className="text-xs text-gray-500">Admin</p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              data-testid="back-to-student-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Student View
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="lessons" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="lessons" data-testid="lessons-tab">Lessons & Exercises</TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>
            <TabsTrigger value="assignments" data-testid="assignments-tab">Assignments</TabsTrigger>
          </TabsList>

          <TabsContent value="lessons">
            <LessonsTab lessons={lessons} onUpdate={loadData} />
          </TabsContent>

          <TabsContent value="users">
            <UsersTab users={users} onUpdate={loadData} />
          </TabsContent>

          <TabsContent value="assignments">
            <AssignmentsTab users={users} lessons={lessons} onUpdate={loadData} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
